import { motion } from 'framer-motion';
import { Check, X } from '@phosphor-icons/react';

import type { Option, PollType } from '@/types/poll';

import styles from './VoteOption.module.css';

const OPTION_COLORS = [
    'var(--option-color-1)',
    'var(--option-color-2)',
    'var(--option-color-3)',
    'var(--option-color-4)',
    'var(--option-color-5)',
    'var(--option-color-6)',
];

interface VoteOptionProps {
    option: Option;
    pollType: PollType;
    totalVotes: number;
    isSelected: boolean;
    hasVoted: boolean;
    disabled: boolean;
    onSelect: (optionId: number) => void;
    index: number;
    isQuiz?: boolean;
}

export function VoteOption({
    option,
    totalVotes,
    isSelected,
    hasVoted,
    disabled,
    onSelect,
    index,
    isQuiz = false,
}: VoteOptionProps) {
    const percentage = totalVotes > 0
        ? (option.vote_count / totalVotes) * 100
        : 0;

    const colorIndex = index % OPTION_COLORS.length;
    const bgColor = OPTION_COLORS[colorIndex];

    const isCorrectOption = isQuiz && hasVoted && option.is_correct === true;
    const isWrongChoice = isQuiz && hasVoted && isSelected && option.is_correct === false;
    const isDimmed = isQuiz && hasVoted && !isSelected && !option.is_correct;

    const handleClick = () => {
        if (!disabled) {
            onSelect(option.id);
        }
    };

    const shakeAnimation = isWrongChoice
        ? { x: [0, -8, 8, -6, 6, -3, 3, 0] }
        : {};

    const bounceAnimation = isCorrectOption && isSelected
        ? { scale: [1, 1.05, 1] }
        : {};

    return (
        <motion.button
            className={`${styles.option} ${isSelected && !hasVoted ? styles.selected : ''} ${isCorrectOption ? styles.correct : ''} ${isWrongChoice ? styles.wrong : ''} ${isDimmed ? styles.dimmed : ''}`}
            style={{ backgroundColor: bgColor } as React.CSSProperties}
            onClick={handleClick}
            disabled={disabled}
            whileTap={disabled ? {} : { scale: 0.97 }}
            animate={{
                ...shakeAnimation,
                ...bounceAnimation,
            }}
            transition={{ duration: 0.4 }}
            type="button"
        >
            <div className={styles.content}>
                <span className={styles.text}>{option.text}</span>
                <div className={styles.right}>
                    {isSelected && !hasVoted && (
                        <span className={styles.checkIcon}>
                            <Check size={20} weight="bold" />
                        </span>
                    )}
                    {hasVoted && isSelected && !isQuiz && (
                        <span className={styles.badge}>Tu voto</span>
                    )}
                    {isCorrectOption && (
                        <span className={styles.correctBadge}>
                            <Check size={14} weight="bold" /> Correcta
                        </span>
                    )}
                    {isWrongChoice && (
                        <span className={styles.wrongBadge}>
                            <X size={14} weight="bold" />
                        </span>
                    )}
                </div>
            </div>

            {hasVoted && !isQuiz && (
                <div className={styles.progressTrack}>
                    <motion.div
                        className={styles.progressFill}
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{
                            duration: 0.6,
                            delay: index * 0.1,
                            ease: 'easeOut',
                        }}
                    />
                </div>
            )}

            {hasVoted && (
                <div className={styles.stats}>
                    <span>{Math.round(percentage)}%</span>
                    <span>{option.vote_count} votos</span>
                </div>
            )}
        </motion.button>
    );
}
